import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Divider,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CloseIconButton from '../../../../components/common/CloseIconButton';
import useCurrency from '../../../../components/CurrencyProvider';
import { getTodayYMD } from '../../../../utils/dateUtils';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Screen prefix for test IDs following naming convention: <screen>-<component>-<intent> */
const TEST_ID_PREFIX = 'collect-package-advance';

/** Default value for advance against dropdown */
const DEFAULT_ADVANCE_AGAINST = 'Package Service';

/** Available options for advance against dropdown */
const ADVANCE_AGAINST_OPTIONS = [
  { value: 'Package Service', label: 'Package Service' },
] as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate the auto-fill amount for advance payment
 * Uses "Balance to Collect" = packageNetAmount - packageAdvanceCollected
 * For Package Closer flow, uses requiredAdvanceAmount if available
 * Rounds to 2 decimal places to avoid floating-point precision issues
 */
const getAutoFillAmount = (pkg: PackageOption): number => {
  // For Package Closer flow, use requiredAdvanceAmount if available
  if (pkg.requiredAdvanceAmount !== undefined && pkg.requiredAdvanceAmount > 0) {
    return Math.round(pkg.requiredAdvanceAmount * 100) / 100;
  }
  // Otherwise use Balance to Collect
  const balanceToCollect = pkg.packageNetAmount - pkg.packageAdvanceCollected;
  return Math.round(Math.max(0, balanceToCollect) * 100) / 100;
};

/**
 * Calculate payment progress as a percentage (0-100)
 * Capped at 100% to prevent overflow display issues
 */
const calculatePaymentProgress = (collected: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.min((collected / total) * 100, 100);
};

/**
 * Parse advance amount string to number, returns 0 for invalid values
 */
const parseAdvanceAmount = (value: string): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// ============================================================================
// INTERFACES       
// ============================================================================

/**
 * Props for the CollectPackageAdvanceModal component
 */
export interface CollectPackageAdvanceModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed (cancel or X button) */
  onClose: () => void;
  /** Callback when payment is collected with form data */
  onCollectPayment: (data: PackageAdvanceFormData) => void;
  /** List of available packages to collect advance for */
  packages: PackageOption[];
  /** Optional: Package ID to pre-select when modal opens (e.g., during package closure) */
  initialSelectedPackageId?: number;
}

/**
 * Package option data structure
 */
export interface PackageOption {
  /** Unique package identifier */
  id: number;
  /** Display name of the package */
  name: string;
  /** Package start date (ISO UTC string) */
  startDate?: string;
  /** Total package amount */
  packageAmount: number;
  /** Net amount after concessions */
  packageNetAmount: number;
  /** Total package concession amount */
  packageConcessionAmount: number;
  /** Total service concession amount */
  totalServiceConcessionAmount: number;
  /** Amount already collected as package advance */
  packageAdvanceCollected: number;
  /** Total advance collected */
  advanceCollected: number;
  /** Amount already used from package */
  packageUsedAmount: number;
  /** Remaining package balance */
  packageBalanceAmount: number;
  /** Service component - remaining available balance */
  serviceRemainingAmount: number;
  /** Pharmacy component - remaining available balance */
  pharmacyRemainingAmount: number;
  /** Service component - total allocated amount */
  serviceAmount: number;
  /** Pharmacy component - total allocated amount */
  pharmacyAmount: number;
  /** Pharmacy component amount */
  pharmacyComponentAmount: number;
  /** Pharmacy component percent */
  pharmacyComponentPercent: number;
  /** Collected pharmacy advance amount */
  collectedPharmacyAdvanceAmount: number;
  /** Remaining pharmacy advance amount to be collected */
  remainingPharmacyAdvanceAmount: number;
  /** Required advance amount from validation API - used to auto-populate advance amount field */
  requiredAdvanceAmount?: number;
  /** Recipient patient ID — the owner of the package (used when donor collects advance) */
  recipientPatientId?: number;
}

/**
 * Form data submitted when collecting package advance payment
 */
export interface PackageAdvanceFormData {
  /** Selected package ID */
  selectedPackageId: number;
  /** Package display name */
  packageName: string;
  /** Total package amount */
  packageAmount: number;
  /** Previously collected advance amount */
  packageAdvanceCollected: number;
  /** Remaining balance before this payment */
  remainingPharmacyAdvanceAmount: number;
  /** Type of advance (e.g., 'Package Service') */
  advanceAgainst: string;
  /** Date of advance collection (YYYY-MM-DD format) */
  advanceDate: string;
  /** Amount being collected in this transaction */
  advanceAmount: number;
  /** Optional remark/note for the payment */
  remark: string;
  /** Recipient patient ID from the selected package (used when donor collects advance for recipient's package) */
  recipientPatientId?: number;
}

/**
 * Modal component for collecting package advance payments.
 * 
 * Features:
 * - Package selection with auto-fill of advance amount
 * - Payment progress bar showing collection status
 * - Currency-formatted amount input
 * - Form validation with error states
 * 
 * @example
 * ```tsx
 * <CollectPackageAdvanceModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onCollectPayment={(data) => handlePayment(data)}
 *   packages={availablePackages}
 * />
 * ```
 */
const CollectPackageAdvanceModal = ({
  open,
  onClose,
  onCollectPayment,
  packages,
  initialSelectedPackageId,
}: CollectPackageAdvanceModalProps) => {
  const { formatCurrency, currencySymbol } = useCurrency();
  
  // ============================================================================
  // FORM STATE
  // ============================================================================
  const [selectedPackageId, setSelectedPackageId] = useState<number | ''>('');
  const [advanceAgainst, setAdvanceAgainst] = useState<string>(DEFAULT_ADVANCE_AGAINST);
  const [advanceDate, setAdvanceDate] = useState<string>(getTodayYMD());
  const [advanceAmount, setAdvanceAmount] = useState<string>('');
  const [remark, setRemark] = useState<string>('');

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId);

  // Calculate payment progress percentage using helper function
  // Use packageNetAmount for consistency with auto-fill amount calculation
  const paymentProgress = selectedPackage 
    ? calculatePaymentProgress(selectedPackage.packageAdvanceCollected, selectedPackage.packageNetAmount)
    : 0;

  // Auto-select package when modal opens:
  // - If initialSelectedPackageId is provided and exists in packages, use it (e.g., during package closure)
  // - Otherwise, fall back to the first package
  useEffect(() => {
    if (open && packages.length > 0) {
      // Try to find the initial package if provided
      const initialPackage = initialSelectedPackageId 
        ? packages.find((pkg) => pkg.id === initialSelectedPackageId)
        : null;
      
      // Use initial package if found, otherwise default to first package
      const packageToSelect = initialPackage || packages[0];
      setSelectedPackageId(packageToSelect.id);
      
      // Auto-fill advance amount using helper function
      const autoFillAmount = getAutoFillAmount(packageToSelect);
      setAdvanceAmount(String(autoFillAmount));
    }
  }, [open, packages, initialSelectedPackageId]);

  // Update advance amount when package selection changes
  const handlePackageChange = (newPackageId: number): void => {
    setSelectedPackageId(newPackageId);
    const pkg = packages.find((p) => p.id === newPackageId);
    if (pkg) {
      const autoFillAmount = getAutoFillAmount(pkg);
      setAdvanceAmount(String(autoFillAmount));
    }
  };

  /** Reset form state and close modal */
  const handleClose = (): void => {
    setSelectedPackageId('');
    setAdvanceAgainst(DEFAULT_ADVANCE_AGAINST);
    setAdvanceDate(getTodayYMD());
    setAdvanceAmount('');
    setRemark('');
    onClose();
  };

  /** Validate and submit payment form data */
  const handleCollectPayment = (): void => {
    // Guard clause: ensure required data is available
    if (!selectedPackage || !advanceAmount) {
      return;
    }

    const formData: PackageAdvanceFormData = {
      selectedPackageId: selectedPackage.id,
      packageName: selectedPackage.name,
      packageAmount: selectedPackage.packageAmount,
      packageAdvanceCollected: selectedPackage.packageAdvanceCollected,
      remainingPharmacyAdvanceAmount: selectedPackage.remainingPharmacyAdvanceAmount,
      advanceAgainst,
      advanceDate,
      advanceAmount: parseAdvanceAmount(advanceAmount),
      remark,
      recipientPatientId: selectedPackage.recipientPatientId,
    };

    onCollectPayment(formData);
  };

  // Form validation: package must be selected, amount must be positive, and not exceed balance
  // Use packageNetAmount for consistency with auto-fill amount calculation
  const balanceToCollect = selectedPackage 
    ? selectedPackage.packageNetAmount - selectedPackage.packageAdvanceCollected 
    : 0;
  const parsedAmount = parseAdvanceAmount(advanceAmount);
  const exceedsBalance = parsedAmount > balanceToCollect && balanceToCollect > 0;
  
  const isFormValid = 
    selectedPackageId !== '' && 
    advanceAmount !== '' && 
    parsedAmount > 0 &&
    !exceedsBalance;

  // Determine error state and message for advance amount field
  const getAmountError = (): { hasError: boolean; message: string; type: 'error' | 'warning' } => {
    if (advanceAmount === '') return { hasError: false, message: '', type: 'error' };
    if (parsedAmount <= 0) return { hasError: true, message: 'Amount must be greater than 0', type: 'error' };
    if (exceedsBalance) return { hasError: true, message: `Maximum allowed: ${formatCurrency(balanceToCollect)}`, type: 'warning' };
    return { hasError: false, message: '', type: 'error' };
  };
  const amountError = getAmountError();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={`${TEST_ID_PREFIX}-dialog-title`}
      aria-describedby={`${TEST_ID_PREFIX}-dialog-description`}
      data-testid={`${TEST_ID_PREFIX}-modal`}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogTitle
        id={`${TEST_ID_PREFIX}-dialog-title`}
        sx={{
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h6" 
            component="h1"
            sx={{ fontWeight: 600, fontSize: '18px' }}
          >
            Collect Package Advance
          </Typography>
          <CloseIconButton 
            onClick={handleClose}
            aria-label="Close dialog"
            data-testid={`${TEST_ID_PREFIX}-button-close`}
          />
        </Box>
      </DialogTitle>

      <DialogContent
        id={`${TEST_ID_PREFIX}-dialog-description`}
        sx={{
          mt: 2,
        }}
      >
        <Box 
          component="form"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          role="form"
          aria-label="Package advance payment form"
        >
          {/* Select Package Section */}
          <Divider />
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id={`${TEST_ID_PREFIX}-select-package-label`}>
                Select Package
              </InputLabel>
              <Select
                labelId={`${TEST_ID_PREFIX}-select-package-label`}
                id={`${TEST_ID_PREFIX}-select-package`}
                label="Select Package"
                value={selectedPackageId}
                onChange={(e) => handlePackageChange(e.target.value as number)}
                sx={{ borderRadius: '8px' }}
                data-testid={`${TEST_ID_PREFIX}-select-package`}
                aria-required="true"
              >
                {packages.map((pkg) => (
                  <MenuItem 
                    key={pkg.id} 
                    value={pkg.id}
                    data-testid={`${TEST_ID_PREFIX}-package-option-${pkg.id}`}
                  >
                    {pkg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Package Details Summary Section */}
          {selectedPackage && (
            <Box
              component="section"
              aria-labelledby={`${TEST_ID_PREFIX}-package-details-heading`}
              data-testid={`${TEST_ID_PREFIX}-package-details`}
              sx={{
                backgroundColor: '#fafafa',
                borderRadius: '10px',
                p: 2,
              }}
            >
              {/* Header with Service/Pharmacy breakdown inline */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Package Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1976d2', flexShrink: 0 }} />
                    Services: <span style={{ color: '#ed6c02', fontWeight: 600 }}>{formatCurrency((selectedPackage.serviceAmount ?? 0) - (selectedPackage.serviceRemainingAmount ?? 0))}</span>
                    <span style={{ color: '#9e9e9e' }}>/</span>
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>{formatCurrency(selectedPackage.serviceRemainingAmount ?? 0)}</span>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#e91e63', flexShrink: 0 }} />
                    Pharmacy: <span style={{ color: '#ed6c02', fontWeight: 600 }}>{formatCurrency((selectedPackage.pharmacyAmount ?? 0) - (selectedPackage.pharmacyRemainingAmount ?? 0))}</span>
                    <span style={{ color: '#9e9e9e' }}>/</span>
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>{formatCurrency(selectedPackage.pharmacyRemainingAmount ?? 0)}</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '10px', ml: 0.5 }}>
                    (<span style={{ color: '#ed6c02' }}>used</span>/<span style={{ color: '#2e7d32' }}>available</span>)
                  </Typography>
                </Box>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={paymentProgress}
                  sx={{
                    height: 5,
                    borderRadius: 2,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      backgroundColor: paymentProgress >= 100 ? '#4caf50' : '#1976d2',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(selectedPackage.packageAdvanceCollected)} collected ({paymentProgress.toFixed(0)}%)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(selectedPackage.packageNetAmount)} total
                  </Typography>
                </Box>
              </Box>

              {/* Balance & Required Amount Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Balance to Collect</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                    {formatCurrency(selectedPackage.packageNetAmount - selectedPackage.packageAdvanceCollected)}
                  </Typography>
                </Box>
                {selectedPackage.requiredAdvanceAmount !== undefined && selectedPackage.requiredAdvanceAmount > 0 && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Required Advance</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                      {formatCurrency(selectedPackage.requiredAdvanceAmount)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Payment Form Fields Section */}
          <Grid2 container spacing={2}>
            {/* Advance Against Field */}
            <Grid2 size={{ xs: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id={`${TEST_ID_PREFIX}-advance-against-label`}>
                  Advance Against
                </InputLabel>
                <Select
                  labelId={`${TEST_ID_PREFIX}-advance-against-label`}
                  id={`${TEST_ID_PREFIX}-advance-against`}
                  label="Advance Against"
                  value={advanceAgainst}
                  onChange={(e) => setAdvanceAgainst(e.target.value)}
                  data-testid={`${TEST_ID_PREFIX}-select-advance-against`}
                  disabled
                >
                  {ADVANCE_AGAINST_OPTIONS.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      data-testid={`${TEST_ID_PREFIX}-advance-against-option-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            {/* Advance Date Field */}
            <Grid2 size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="date"
                size="small"
                id={`${TEST_ID_PREFIX}-advance-date`}
                label="Amount Date"
                value={advanceDate}
                onChange={(e) => setAdvanceDate(e.target.value)}
                data-testid={`${TEST_ID_PREFIX}-input-advance-date`}
                disabled
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  htmlInput: {
                    min: getTodayYMD(),
                    'aria-label': 'Advance payment date',
                  },
                }}
              />
            </Grid2>

            {/* Advance Amount Field */}
            <Grid2 size={{ xs: 4 }}>
              <TextField
                fullWidth
                type="number"
                size="small"
                id={`${TEST_ID_PREFIX}-advance-amount`}
                label="Advance Amount"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                required
                aria-required="true"
                data-testid={`${TEST_ID_PREFIX}-input-advance-amount`}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        {currencySymbol}
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    max: balanceToCollect,
                    step: 0.01,
                  },
                  formHelperText: {
                    sx: { 
                      mx: 0, 
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    },
                  },
                }}
                error={amountError.hasError && amountError.type === 'error'}
                helperText={amountError.hasError ? (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    sx={{ 
                      color: amountError.type === 'warning' ? '#ed6c02' : '#d32f2f',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '11px',
                    }}
                  >
                    {amountError.type === 'warning' ? '⚠' : '✕'} {amountError.message}
                  </Typography>
                ) : null}
                sx={{
                  '& .MuiOutlinedInput-root': amountError.hasError && amountError.type === 'warning' ? {
                    '& fieldset': { borderColor: '#ed6c02' },
                    '&:hover fieldset': { borderColor: '#e65100' },
                    '&.Mui-focused fieldset': { borderColor: '#ed6c02' },
                  } : {},
                }}
              />
            </Grid2>
          </Grid2>

          {/* Remark Field */}
          <Box>
            <TextField
              fullWidth
              size="small"
              id={`${TEST_ID_PREFIX}-remark`}
              label="Remark"
              name="remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              multiline
              minRows={2}
              maxRows={3}
              placeholder="Type Here"
              data-testid={`${TEST_ID_PREFIX}-input-remark`}
              slotProps={{
                htmlInput: {
                  'aria-label': 'Payment remark or note',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button 
          variant="outlined" 
          onClick={handleClose} 
          color="inherit"
          sx={{ borderRadius: '8px', px: 3 }}
          data-testid={`${TEST_ID_PREFIX}-button-cancel`}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCollectPayment}
          disabled={!isFormValid}
          sx={{
            borderRadius: '8px',
            px: 3,
            minWidth: 120,
          }}
          data-testid={`${TEST_ID_PREFIX}-button-submit`}
          aria-disabled={!isFormValid}
        >
          Collect Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectPackageAdvanceModal;
